// src/pages/Hub/components/HealthRail.js
//
// Left rail: animated company-health ring + KPI count-ups + per-region completion
// bars. Pure presentation — fed the /api/ops-hub/overview payload.

import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, CheckCircle2, AlertTriangle, Banknote, Layers } from 'lucide-react';
import { C, regionMeta, fmtMoney } from '../theme';

function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const ref = useRef({ from: 0, start: 0 });
  useEffect(() => {
    const to = Number(target) || 0;
    ref.current.from = val;
    ref.current.start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - ref.current.start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(ref.current.from + (to - ref.current.from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);
  return val;
}

function ScoreRing({ score }) {
  const animated = useCountUp(score == null ? 0 : score, 1100);
  const radius = 50;
  const circ = 2 * Math.PI * radius;
  const pct = (score == null ? 0 : animated) / 100;
  const color = score == null ? C.faint : score >= 80 ? C.green : score >= 55 ? C.amber : C.red;
  return (
    <div className="hub-ring">
      <svg width="116" height="116" viewBox="0 0 116 116">
        <circle cx="58" cy="58" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="9" />
        <circle
          cx="58" cy="58" r={radius} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} transform="rotate(-90 58 58)"
          style={{ filter: `drop-shadow(0 0 6px ${color})`, transition: 'stroke 0.4s' }}
        />
      </svg>
      <div className="hub-ring-num">
        <div className="hub-ring-score" style={{ color }}>{score == null ? '—' : Math.round(animated)}</div>
        <div className="hub-ring-label">Health</div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, color }) {
  return (
    <div className="hub-kpi">
      <span className="hub-kpi-label">{icon}{label}</span>
      <span className="hub-kpi-val" style={{ color: color || C.text }}>{value}</span>
    </div>
  );
}

export default function HealthRail({ overview, loading }) {
  const totals = overview?.totals || {};
  const revenue = overview?.revenue || {};
  const regions = overview?.regions || [];

  const jobs = useCountUp(totals.total || 0);
  const done = useCountUp(totals.completed || 0);
  const completionPct = Math.round((totals.completionRate || 0) * 100);

  return (
    <div className="hub-rail">
      <div className="hub-card">
        <div className="hub-card-title"><Activity size={14} /> Company Health</div>
        <div className="hub-ring-wrap">
          <ScoreRing score={overview?.health ?? null} />
          <div className="hub-kpis">
            <Kpi icon={<Layers size={14} />} label="Jobs" value={loading ? '…' : Math.round(jobs)} />
            <Kpi icon={<CheckCircle2 size={14} />} label="Completed" value={loading ? '…' : Math.round(done)} color={C.green} />
            <Kpi icon={<AlertTriangle size={14} />} label="Overdue" value={loading ? '…' : (totals.overdue || 0)} color={(totals.overdue || 0) > 0 ? C.red : C.text} />
            <Kpi icon={<Banknote size={14} />} label="Revenue" value={loading ? '…' : fmtMoney((revenue.mtd || 0) + (revenue.contracted || 0))} color={C.green} />
          </div>
        </div>

        <div className="hub-stat-grid">
          <div className="hub-stat">
            <div className="hub-stat-num">{completionPct}%</div>
            <div className="hub-stat-cap">Completion rate</div>
          </div>
          <div className="hub-stat">
            <div className="hub-stat-num" style={{ color: C.green }}>{fmtMoney(revenue.mtd || 0)}</div>
            <div className="hub-stat-cap">Sales (range)</div>
          </div>
        </div>
      </div>

      <div className="hub-card">
        <div className="hub-card-title"><Layers size={14} /> By Region</div>
        <div className="hub-regions">
          {regions.length === 0 && <div style={{ color: C.muted, fontSize: 12.5 }}>No data in range.</div>}
          {regions.map((r) => {
            const meta = regionMeta(r.region);
            const pct = Math.round((r.completionRate || 0) * 100);
            return (
              <div className="hub-region-row" key={r.region}>
                <div className="hub-region-head">
                  <span className="hub-region-name">
                    <span className="hub-region-dot" style={{ background: meta.color, color: meta.color }} />
                    {meta.label}
                  </span>
                  <span className="hub-region-meta">{r.completed}/{r.total} · {pct}%</span>
                </div>
                <div className="hub-bar">
                  <motion.div
                    className="hub-bar-fill"
                    style={{ background: meta.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ type: 'spring', stiffness: 120, damping: 22 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
